import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, Card, Link, Spin, Title, message } from '@bb/elements-web';
import { getBillingProfile } from 'api/BillingController';
import { AuthContext } from 'contexts';
import UpdateBillingContactInfoModal from '../../PaymentConfirmationPage/UpdateBillingContactInfoModal';
import PaymentConfirmationContent from '../../PaymentConfirmationPage';
import { StyledPageContent } from '../StyledComponents';
import AnsweringServiceContext, {
  AnsweringServiceSubscribeStep,
} from '../AnsweringServiceContext';
import type { PaymentConfirmationBillingInfo } from '../../PaymentConfirmationPage/types/paymentConfirmationTypes';
import { useUpdatePaymentCardModalContext } from '../../../components/ManagePaymentCard/UpdatePaymentCardModalContext';
import UpdatePaymentCardModal from '../../../components/ManagePaymentCard/UpdatePaymentCardModal';

/**
 * The page that shows during the Answering Service subscription flow
 * confirming the monetary total with the user and allowing them to make
 * any billing changes they want.
 */
const PaymentConfirmation: React.FC = () => {
  const { auth } = useContext(AuthContext);

  const {
    setCurrentStep,
    subscribeBtnLoading,
    createSubscription,
    viewingLevel,
    setBillingDetails,
  } = useContext(AnsweringServiceContext);

  const { updatedPaymentInfo } = useUpdatePaymentCardModalContext();

  const [openContactDetailsModal, setOpenContactDetailsModal] = useState(false);
  const [fetchingBillingInfo, setFetchingBillingInfo] = useState(true);
  /** The billing information for this user which will be saved to BillingPlatform at the end. */
  const [billingInfo, setBillingInfo] =
    useState<PaymentConfirmationBillingInfo>({
      firstName: auth.firstName || '',
      lastName: auth.lastName || '',
      email: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      jpmcSessionId: '',
      ccNumber: '',
      maskedCCNumber: '',
      ccExpiryDate: '',
      cardholderName: '',
      ccType: '',
    });

  // Ensure all required billing info has been entered into the form.
  const billingInfoComplete = Object.entries(billingInfo)
    .filter(([key]) => key !== 'address2' && key !== 'jpmcSessionId')
    .every(
      ([, value]) => value !== '' && value !== undefined && value !== null,
    );

  useEffect(() => {
    if (updatedPaymentInfo) {
      setBillingInfo(prev => ({
        ...prev,
        jpmcSessionId: updatedPaymentInfo.JPUid || prev.jpmcSessionId,
        ccNumber: updatedPaymentInfo.ccNumber || prev.ccNumber,
        maskedCCNumber:
          updatedPaymentInfo.maskedCCNumber || prev.maskedCCNumber,
        ccExpiryDate: updatedPaymentInfo.ccExpiryDate || prev.ccExpiryDate,
        cardholderName:
          updatedPaymentInfo.cardholderName || prev.cardholderName,
        ccType: updatedPaymentInfo.ccType || prev.ccType,
      }));
    }
  }, [updatedPaymentInfo]);

  // fetch the billing profile for the user/org to set as default values if billing profile already exists.
  useEffect(() => {
    let isMounted = true;

    const fetchBillingInfo = async () => {
      const billingCustomerNumber =
        viewingLevel === 'user'
          ? auth.billingCustomerNumber
          : auth.org?.billingCustomerNumber;

      if (!billingCustomerNumber) {
        setFetchingBillingInfo(false);
        return;
      }

      setFetchingBillingInfo(true);

      try {
        const profile = await getBillingProfile({
          billingCustomerNumber,
        });

        if (isMounted) {
          if (profile) {
            setBillingInfo(prev => ({
              ...prev,
              firstName: profile.FirstName || prev.firstName || '',
              lastName: profile.LastName || prev.lastName || '',
              email: profile.Email || prev.email || '',
              address1: profile.Address1 || prev.address1 || '',
              address2: profile.Address2 || prev.address2 || '',
              city: profile.City || prev.city || '',
              state: profile.State || prev.state || '',
              zipCode: profile.Zip || prev.zipCode || '',
              country: profile.Country || prev.country || '',
              jpmcSessionId: '',
              ccNumber: profile.CCNumber || prev.ccNumber || '',
              maskedCCNumber:
                profile.MaskedCCNumber || prev.maskedCCNumber || '',
              ccExpiryDate: profile.CCExpiryDate || prev.ccExpiryDate || '',
              cardholderName:
                profile.CardholderName || prev.cardholderName || '',
              ccType: profile.CCType || prev.ccType || '',
            }));
          }
        }
      } catch (error) {
        if (isMounted) {
          message.error(
            'Failed to load billing profile. Please refresh the page and try again.',
          );
        }
      } finally {
        if (isMounted) {
          setFetchingBillingInfo(false);
        }
      }
    };

    fetchBillingInfo();

    return () => {
      isMounted = false;
    };
  }, [
    auth,
    auth.address,
    auth.billingCustomerNumber,
    auth.location,
    auth.org?.billingCustomerNumber,
    auth.org?.id,
    viewingLevel,
    setBillingDetails,
  ]);

  const handleCreateSubscription = async () => {
    createSubscription({
      firstName: billingInfo.firstName,
      lastName: billingInfo.lastName,
      email: billingInfo.email,
      address1: billingInfo.address1,
      address2: billingInfo.address2,
      city: billingInfo.city,
      state: billingInfo.state,
      zipCode: billingInfo.zipCode,
      country: billingInfo.country,
      jpmcSessionId: billingInfo.jpmcSessionId,
    });
  };

  return (
    <>
      <UpdateBillingContactInfoModal
        billingInfo={billingInfo}
        setBillingInfo={setBillingInfo}
        open={openContactDetailsModal}
        closeModal={() => setOpenContactDetailsModal(false)}
        handleConfirmContactInfo={() => setOpenContactDetailsModal(false)}
      />

      <UpdatePaymentCardModal showFooter />

      <StyledPageContent>
        {fetchingBillingInfo ? (
          <Spin
            dataCy="payment-confirmation-billing-info-spin"
            size="large"
            spinning
          />
        ) : (
          <StyledCard dataCy="payment-confirmation-card">
            <Title level={3} dataCy="activate-plan-title" margin={0}>
              Payment & Confirmation
            </Title>
            <StyledActivationContainer>
              <PaymentConfirmationContent
                billingInfo={billingInfo}
                handleAddAddressClick={() => {
                  setOpenContactDetailsModal(true);
                }}
                serviceType="liveConcierge"
              />
            </StyledActivationContainer>
            <ConfirmBackContainer>
              <StyledLink
                onClick={
                  subscribeBtnLoading
                    ? undefined
                    : () =>
                        setCurrentStep(
                          AnsweringServiceSubscribeStep.ManageListings,
                        )
                }
                $disabled={subscribeBtnLoading}
                size="large"
              >
                Back
              </StyledLink>
              <Button
                disabled={!billingInfoComplete}
                minWidth="auto"
                type="primary"
                onClick={handleCreateSubscription}
                loading={subscribeBtnLoading}
                dataCy="confirm-pay-btn"
              >
                Confirm
              </Button>
            </ConfirmBackContainer>
          </StyledCard>
        )}
      </StyledPageContent>
    </>
  );
};

const ConfirmBackContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledActivationContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-top: 24px;
`;

const StyledCard = styled(Card)`
  padding: 10px;
  width: 550px;
`;

const StyledLink = styled(Link)<{ $disabled?: boolean }>`
  color: black;
  margin-right: 25px;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

export default PaymentConfirmation;
